import { Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import React, { useState } from "react";
import { NotifyCustomerCheckbox } from "@/components/Common/NotifyCustomerCheckbox";
import { OptionCard } from "@/components/Common/OptionCard";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ConfirmationModalProps } from "@/hooks/useConfirmationModal";

export enum ScheduleOptionsMode {
    KEEP_ON_RESERVED_EVENTS = "keep_on_reserved_events",
    UPDATE_RESERVED_EVENTS = "update_reserved_events",
}

export interface ScheduleOptionChangeChoice {
    optionsMode: ScheduleOptionsMode;
    shouldNotifyCustomer: boolean;
}

export const ScheduleOptionChangeModal = ({
    isOpen,
    onClose,
    onContinue,
}: ConfirmationModalProps<ScheduleOptionChangeChoice | false>) => (
    <Modal shouldCloseOnOutsideClick={false} isOpen={isOpen} onClose={onClose}>
        <ModalContent onClose={onClose} onContinue={onContinue} />
    </Modal>
);

const ModalContent = ({
    onClose,
    onContinue,
}: Pick<ConfirmationModalProps<ScheduleOptionChangeChoice | false>, "onClose" | "onContinue">) => {
    const [optionsMode, setOptionsMode] = useState<ScheduleOptionsMode>(ScheduleOptionsMode.KEEP_ON_RESERVED_EVENTS);
    const [shouldNotifyCustomer, setShouldNotifyCustomer] = useState(false);
    const isUpdateMode = optionsMode === ScheduleOptionsMode.UPDATE_RESERVED_EVENTS;

    const handleKeepMode = useMemoizedFn(() => setOptionsMode(ScheduleOptionsMode.KEEP_ON_RESERVED_EVENTS));
    const handleUpdateMode = useMemoizedFn(() => setOptionsMode(ScheduleOptionsMode.UPDATE_RESERVED_EVENTS));
    const handleNotifyChange = useMemoizedFn((event: React.ChangeEvent<HTMLInputElement>) =>
        setShouldNotifyCustomer(event.target.checked),
    );
    // Keep mode hides the checkbox, so it must not record a notification choice the seller never made.
    const handleSave = useMemoizedFn(() =>
        onContinue({ optionsMode, shouldNotifyCustomer: isUpdateMode ? shouldNotifyCustomer : false }),
    );

    return (
        <>
            <Modal.Header>Update Experience Option</Modal.Header>
            <Modal.Body className="space-y-4 text-gray-darker">
                <p>
                    Updating this schedule will automatically apply the new option to all future unreserved events.
                    However, some future events already have active reservations.
                </p>
                <p className="font-bold">How would you like to handle future events with reservations?</p>
                <OptionCard
                    isSelected={optionsMode === ScheduleOptionsMode.KEEP_ON_RESERVED_EVENTS}
                    testId="schedule-option-change-mode-keep"
                    title="Keep current option on reserved future events"
                    description="Events with existing reservations will keep their original option. Only unreserved future events will be updated."
                    onClick={handleKeepMode}
                />
                <OptionCard
                    isSelected={isUpdateMode}
                    testId="schedule-option-change-mode-update"
                    title="Update all future events"
                    description="Apply this new option to every future event, including those with active reservations."
                    onClick={handleUpdateMode}
                />
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-between">
                    {isUpdateMode ? (
                        <NotifyCustomerCheckbox isChecked={shouldNotifyCustomer} onChange={handleNotifyChange} />
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center space-x-4">
                        <SecondaryButton data-testid="schedule-option-change-cancel-button" onClick={onClose}>
                            Cancel
                        </SecondaryButton>
                        <SubmitButton data-testid="schedule-option-change-confirm-button" onClick={handleSave}>
                            Save
                        </SubmitButton>
                    </div>
                </div>
            </Modal.Footer>
        </>
    );
};
